import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articleData, allArticleIds } from '@/mocks/articleData';
import { getWebPageSchema, getBreadcrumbSchema, getArticleSchema } from '@/components/base/SchemaOrg';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [commentText, setCommentText] = useState('');
  const [comments, setComments] = useState<typeof articleData[string]['comments']>([]);
  const [commentSubmitted, setCommentSubmitted] = useState(false);

  if (!id || !allArticleIds.includes(id)) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <i className="ri-file-search-line text-4xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">文章不存在</h1>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 transition-colors">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const fallbackArticle = Object.values(articleData)[0];
  const article = articleData[id] || fallbackArticle;
  if (!article) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">文章加载失败</h1>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 transition-colors">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const displayComments = comments.length > 0 ? comments : article.comments;
  const pageTitle = `${article.title} - 51吃瓜app`;
  const pageUrl = `http://shlvtian.cn/article/${id}`;

  const handleLike = () => {
    if (!liked) {
      setLiked(true);
      setLikeCount((c) => c + 1);
    }
  };

  const handleCommentSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    const newComment = {
      id: `cmt-new-${Date.now()}`,
      userName: '匿名吃瓜群众',
      userAvatar: 'https://readdy.ai/api/search-image?query=Anonymous%20mask%20cartoon%20avatar%2C%20mystery%20theme%2C%20warm%20gray%20colors%2C%20simple%20illustration&width=100&height=100&seq=avatar-anon&orientation=squarish',
      content: commentText.trim(),
      likes: 0,
      publishDate: new Date().toISOString().split('T')[0],
    };
    setComments([newComment, ...displayComments]);
    setCommentText('');
    setCommentSubmitted(true);
    setTimeout(() => setCommentSubmitted(false), 3000);
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(getWebPageSchema(pageTitle, article.subtitle, pageUrl, article.publishDate)),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(
            getBreadcrumbSchema([
              { name: '首页', url: 'http://shlvtian.cn/' },
              { name: article.category, url: 'http://shlvtian.cn/' },
              { name: article.title, url: pageUrl },
            ])
          ),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(
            getArticleSchema(article.title, article.subtitle, pageUrl, article.publishDate, article.author, article.mainImage)
          ),
        }}
      />

      <Navbar />

      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', path: '/' },
            { label: article.category, path: '/' },
            { label: article.title },
          ]}
        />

        <article className="w-full px-4 md:px-6 py-6 md:py-10">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 mb-8">
              <div className="lg:w-[40%] flex flex-col justify-center">
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <Link
                    to="/"
                    className="px-2.5 py-1 text-xs bg-secondary-100 text-secondary-700 rounded-full hover:bg-secondary-200 transition-colors"
                  >
                    {article.category}
                  </Link>
                  {article.tags.map((tag) => (
                    <span key={tag} className="px-2.5 py-1 text-xs bg-background-100 text-foreground-600 rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>

                <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 leading-tight mb-2">
                  {article.title}
                </h1>
                <p className="text-lg md:text-xl font-heading italic text-foreground-700 mb-4">
                  {article.subtitle}
                </p>

                <div className="flex items-center gap-3 text-xs text-foreground-500 mb-4 flex-wrap">
                  <span className="flex items-center gap-1">
                    <div className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-user-line"></i>
                    </div>
                    {article.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <div className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-calendar-line"></i>
                    </div>
                    {article.publishDate}
                  </span>
                  <span className="flex items-center gap-1">
                    <div className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-eye-line"></i>
                    </div>
                    {(article.viewCount / 10000).toFixed(0)}万阅读
                  </span>
                  <span>{article.readTime}</span>
                </div>

                <div className="flex items-center gap-4">
                  <button
                    onClick={handleLike}
                    className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      liked
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-500'
                    }`}
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={`${liked ? 'ri-heart-fill' : 'ri-heart-line'}`}></i>
                    </div>
                    {article.hotScore + likeCount > 0
                      ? `${((article.hotScore + likeCount) / 10000).toFixed(1)}万`
                      : '点赞'}
                  </button>

                  <button
                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-background-100 text-foreground-600 rounded-full text-sm font-medium hover:bg-secondary-50 hover:text-secondary-600 transition-colors whitespace-nowrap cursor-pointer"
                    onClick={() => {
                      const el = document.getElementById('comments-section');
                      el?.scrollIntoView({ behavior: 'smooth' });
                    }}
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-chat-3-line"></i>
                    </div>
                    {displayComments.length} 评论
                  </button>
                </div>
              </div>

              <div className="lg:w-[60%]">
                <div className="relative w-full aspect-[16/10] rounded-2xl overflow-hidden">
                  <img
                    src={article.mainImage}
                    alt={article.title}
                    title={`${article.title} - 51吃瓜app`}
                    className="w-full h-full object-cover object-top"
                    loading="eager"
                  />
                </div>
              </div>
            </div>

            <div className="bg-background-100/50 rounded-2xl px-6 md:px-12 py-8 md:py-12 mb-10">
              <div className="prose max-w-none">
                <p className="text-xs text-foreground-400 text-center mb-6 uppercase tracking-wider">独家报道</p>
                {article.content.map((paragraph, i) => (
                  <p
                    key={i}
                    className="text-base md:text-lg text-foreground-800 leading-relaxed mb-5 text-justify font-heading"
                  >
                    {paragraph}
                    {i === article.content.length - 1 && (
                      <span className="text-accent-500 ml-1">🍉</span>
                    )}
                  </p>
                ))}
              </div>
            </div>

            {article.relatedImages.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
                {article.relatedImages.map((img, i) => (
                  <figure key={i} className="rounded-xl overflow-hidden">
                    <div className="relative w-full aspect-[3/2]">
                      <img
                        src={img.url}
                        alt={img.caption}
                        title={img.caption}
                        className="w-full h-full object-cover object-top"
                        loading="lazy"
                      />
                    </div>
                    <figcaption className="text-xs text-foreground-500 mt-2 text-center">
                      {img.caption}
                    </figcaption>
                  </figure>
                ))}
              </div>
            )}

            <div className="relative w-full h-[420px] md:h-[500px] flex items-center justify-center overflow-hidden rounded-2xl mb-10">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20atmospheric%20abstract%20background%20with%20soft%20gradient%20of%20coral%20and%20amber%20tones%2C%20dreamy%20bokeh%20effect%2C%20warm%20light%20particles%2C%20modern%20aesthetic%2C%20soft%20diffused%20glow%2C%20abstract%20photography&width=1920&height=800&seq=cta-article-bg&orientation=landscape"
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-foreground-900/40 via-foreground-900/30 to-foreground-900/50"></div>
              <div className="relative z-10 text-center px-4 max-w-2xl mx-auto">
                <h2 className="text-2xl md:text-4xl font-heading font-bold text-background-50 mb-3 tracking-wide uppercase">
                  还想看更多爆料？
                </h2>
                <p className="text-sm md:text-base text-background-50/80 mb-6">
                  下载51吃瓜app，海量爆料每日更新
                </p>
                <a
                  href="#"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-foreground-900 text-background-50 text-sm font-medium rounded-full hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <div className="w-6 h-6 rounded-full bg-accent-500 flex items-center justify-center flex-shrink-0">
                    <div className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-download-line text-background-50 text-xs"></i>
                    </div>
                  </div>
                  <span>立即下载 App</span>
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-up-line text-sm"></i>
                  </div>
                </a>
              </div>
            </div>

            <section id="comments-section" className="bg-background-100/50 rounded-2xl px-6 md:px-10 py-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-heading font-bold text-foreground-900">
                  <span className="text-foreground-400 font-normal">[</span>
                  热门评论
                  <span className="text-foreground-400 font-normal">]</span>
                </h2>
                <span className="px-2.5 py-1 text-xs bg-foreground-900 text-background-50 rounded-full flex items-center gap-1">
                  <div className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-star-fill text-accent-400 text-[10px]"></i>
                  </div>
                  {article.hotScore / 10000 >= 1
                    ? `${(article.hotScore / 10000).toFixed(1)}`
                    : article.hotScore}
                </span>
              </div>

              <div className="space-y-5 mb-8">
                {displayComments.slice(0, 5).map((cmt) => (
                  <div key={cmt.id} className="flex gap-3">
                    <div className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0 bg-background-200">
                      <img
                        src={cmt.userAvatar}
                        alt={cmt.userName}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium text-foreground-900">{cmt.userName}</span>
                        <span className="text-xs text-foreground-500">{cmt.publishDate}</span>
                      </div>
                      <p className="text-sm text-foreground-700 leading-relaxed">{cmt.content}</p>
                      <div className="flex items-center gap-1 mt-1.5 text-xs text-foreground-400">
                        <div className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-thumb-up-line text-[10px]"></i>
                        </div>
                        {cmt.likes}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {commentSubmitted && (
                <div className="mb-4 px-4 py-2.5 bg-accent-50 text-accent-700 text-sm rounded-lg text-center">
                  评论发表成功！
                </div>
              )}

              <form onSubmit={handleCommentSubmit} className="flex gap-3">
                <div className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0 bg-background-200">
                  <img
                    src="https://readdy.ai/api/search-image?query=Anonymous%20mask%20cartoon%20avatar%2C%20mystery%20theme%2C%20warm%20gray%20colors%2C%20simple%20illustration&width=100&height=100&seq=avatar-anon&orientation=squarish"
                    alt="匿名用户"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1 flex flex-col sm:flex-row gap-2">
                  <input
                    type="text"
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    placeholder="发表你的吃瓜看法..."
                    className="flex-1 px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-full text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                    maxLength={500}
                  />
                  <button
                    type="submit"
                    disabled={!commentText.trim()}
                    className="px-5 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap cursor-pointer"
                  >
                    发表
                  </button>
                </div>
              </form>
            </section>

            <div className="mt-10 flex items-center justify-between">
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-background-100 text-foreground-600 text-sm rounded-full hover:bg-secondary-50 hover:text-secondary-600 transition-colors cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-left-line"></i>
                </div>
                返回{article.category}
              </Link>

              <Link
                to="/"
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-background-100 text-foreground-600 text-sm rounded-full hover:bg-primary-50 hover:text-primary-500 transition-colors cursor-pointer"
              >
                返回首页
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </Link>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}